import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  Button,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import { getAuditLogs, getAllAuditLogs } from './functions/get_audit_logs.function';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';

export const AuditLogsPopup = ({ open, onClose, siteCode }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sitesPopup, setSitesPopup] = useState(false);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [popupTitle, setPopupTitle] = useState('');
  const [popupType, setPopupType] = useState(''); // 'new', 'updated', 'deleted'

  useEffect(() => {
    if (open && siteCode && siteCode !== null && siteCode !== undefined) {
      // For site-specific audit logs
      setLoading(true);
      const fetchLogs = async () => {
        try {
          const data = await getAuditLogs(siteCode);
          // Sort logs by created_at in descending order (most recent first)
          const sortedData = data.sort((a, b) => {
            const dateA = new Date(a.created_at || a.timestamp || 0);
            const dateB = new Date(b.created_at || b.timestamp || 0);
            return dateB - dateA;
          });
          setLogs(sortedData);
        } catch (error) {
          console.error('Error fetching audit logs:', error);
          setLogs([]);
        } finally {
          setLoading(false);
        }
      };
      fetchLogs();
    } else if (open && (siteCode === null || siteCode === undefined || siteCode === '')) {
      // For general audit logs popup (audit button click)
      setLoading(true);
      const fetchLogs = async () => {
        try {
          const data = await getAllAuditLogs();
          // Sort logs by created_at in descending order (most recent first)
          const sortedData = data.sort((a, b) => {
            const dateA = new Date(a.created_at || a.timestamp || 0);
            const dateB = new Date(b.created_at || b.timestamp || 0);
            return dateB - dateA;
          });
          setLogs(sortedData);
        } catch (error) {
          console.error('Error fetching audit logs:', error);
          setLogs([]);
        } finally {
          setLoading(false);
        }
      };
      fetchLogs();
    }
  }, [open, siteCode]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    // Ensure we always show the full time format
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  const groupLogsByDate = (logs) => {
    const grouped = {};
    logs.forEach((log) => {
      const date = formatDate(log.created_at || log.timestamp || new Date());
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(log);
    });

    // Sort logs within each date group by time (most recent first)
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        const dateA = new Date(a.created_at || a.timestamp || 0);
        const dateB = new Date(b.created_at || b.timestamp || 0);
        return dateB - dateA;
      });
    });

    return grouped;
  };

  const handlePrint = () => {
    // Prepare audit logs data for Excel export with detailed field data
    const excelData = [];

    logs.forEach((log) => {
      const details = log.details || {};

      // Extract counts from details
      let newCount = 0;
      let updatedCount = 0;
      let deletedCount = 0;

      if (details.newSite !== undefined || details.newSites !== undefined) {
        const newData = details.newSite || details.newSites;
        newCount = Array.isArray(newData) ? newData.length : newData;
      }

      if (details.updatedSites !== undefined) {
        updatedCount = Array.isArray(details.updatedSites)
          ? details.updatedSites.length
          : details.updatedSites;
      }

      if (
        details.deletedSite !== undefined ||
        details.deleteSites !== undefined ||
        details.deletedSites !== undefined
      ) {
        const deletedData = details.deletedSite || details.deleteSites || details.deletedSites;
        deletedCount = Array.isArray(deletedData) ? deletedData.length : deletedData;
      }

      // Base audit log entry
      const baseEntry = {
        Date: formatDate(log.created_at || log.timestamp || new Date()),
        Time: formatTime(log.created_at || log.timestamp || new Date()),
        User: log.username || log.user || 'Unknown User',
        Action: log.summary || log.action || log.description || 'Action performed',
        'New Sites': newCount,
        'Updated Sites': updatedCount,
        'Deleted Sites': deletedCount,
        'Total Changes': newCount + updatedCount + deletedCount,
        'Site Code': siteCode || 'All Sites',
        'Created At': log.created_at || log.timestamp || '',
        ID: log.id || '',
      };

      // Get the actual site data arrays
      const newSitesData = Array.isArray(details.newSite || details.newSites)
        ? details.newSite || details.newSites
        : [];
      const updatedSitesData = Array.isArray(details.updatedSites) ? details.updatedSites : [];
      const deletedSitesData = Array.isArray(
        details.deletedSite || details.deleteSites || details.deletedSites
      )
        ? details.deletedSite || details.deleteSites || details.deletedSites
        : [];

      // If there are no site details, just add the base entry
      if (
        newSitesData.length === 0 &&
        updatedSitesData.length === 0 &&
        deletedSitesData.length === 0
      ) {
        excelData.push(baseEntry);
        return;
      }

      // Add entries for new sites
      newSitesData.forEach((site, index) => {
        excelData.push({
          ...baseEntry,
          'Change Type': 'New Site',
          'Site Index': index + 1,
          'Site Code': site.site_code || 'N/A',
          'Site Status': site.site_status || 'N/A',
          Region: site.region || 'N/A',
          Vendor: site.vendor || 'N/A',
          Physical: site.physical || 'N/A',
          'Coverage Type': site.coverage_type || 'N/A',
          BSC: site.bsc || 'N/A',
          'BSC Code': site.bsc_code || 'N/A',
          'Commercial City': site.commercial_city || 'N/A',
          'Commercial District': site.commercial_district || 'N/A',
          'Commercial Region': site.commercial_region || 'N/A',
          'Commercial Subregion': site.commercial_subregion || 'N/A',
          'Technical City': site.technical_city || 'N/A',
          MBU: site.mbu || 'N/A',
          'PTA District': site.pta_district || 'N/A',
          'PTA Province': site.pta_province || 'N/A',
          'PTA Tehsil': site.pta_tehsil || 'N/A',
          'USF Site': site.usf_site || 'N/A',
          'Locked Status': site.locked_status || 'N/A',
          Archived: site.archived || 'N/A',
          'Archived Type': site.archived_type || 'N/A',
          'Franchise ID': site.franchise_id || 'N/A',
          Prefix: site.prefix || 'N/A',
          'Site From': site.site_from || 'N/A',
          'Site To': site.site_to || 'N/A',
          'OMO Name': site.omo_name || 'N/A',
          'Sharing Type Host/Guest': site.sharing_type_hostguest || 'N/A',
          'Part of Dec CFO': site.part_of_dec_cfo || 'N/A',
          'City Prefix Based': site.city_prefix_based || 'N/A',
          '2G Locked Tag': site['2g_locked_tag'] || 'N/A',
          '4G Locked Tag': site['4g_locked_tag'] || 'N/A',
          Latitude: site.lat || 'N/A',
          Longitude: site.long || 'N/A',
          Status: site.status || 'N/A',
        });
      });

      // Add entries for updated sites
      updatedSitesData.forEach((site, index) => {
        excelData.push({
          ...baseEntry,
          'Change Type': 'Updated Site',
          'Site Index': index + 1,
          'Site Code': site.site_code || 'N/A',
          'Site Status': site.site_status || 'N/A',
          Region: site.region || 'N/A',
          Vendor: site.vendor || 'N/A',
          Physical: site.physical || 'N/A',
          'Coverage Type': site.coverage_type || 'N/A',
          BSC: site.bsc || 'N/A',
          'BSC Code': site.bsc_code || 'N/A',
          'Commercial City': site.commercial_city || 'N/A',
          'Commercial District': site.commercial_district || 'N/A',
          'Commercial Region': site.commercial_region || 'N/A',
          'Commercial Subregion': site.commercial_subregion || 'N/A',
          'Technical City': site.technical_city || 'N/A',
          MBU: site.mbu || 'N/A',
          'PTA District': site.pta_district || 'N/A',
          'PTA Province': site.pta_province || 'N/A',
          'PTA Tehsil': site.pta_tehsil || 'N/A',
          'USF Site': site.usf_site || 'N/A',
          'Locked Status': site.locked_status || 'N/A',
          Archived: site.archived || 'N/A',
          'Archived Type': site.archived_type || 'N/A',
          'Franchise ID': site.franchise_id || 'N/A',
          Prefix: site.prefix || 'N/A',
          'Site From': site.site_from || 'N/A',
          'Site To': site.site_to || 'N/A',
          'OMO Name': site.omo_name || 'N/A',
          'Sharing Type Host/Guest': site.sharing_type_hostguest || 'N/A',
          'Part of Dec CFO': site.part_of_dec_cfo || 'N/A',
          'City Prefix Based': site.city_prefix_based || 'N/A',
          '2G Locked Tag': site['2g_locked_tag'] || 'N/A',
          '4G Locked Tag': site['4g_locked_tag'] || 'N/A',
          Latitude: site.lat || 'N/A',
          Longitude: site.long || 'N/A',
          Status: site.status || 'N/A',
        });
      });

      // Add entries for deleted sites
      deletedSitesData.forEach((site, index) => {
        excelData.push({
          ...baseEntry,
          'Change Type': 'Deleted Site',
          'Site Index': index + 1,
          'Site Code': site.site_code || site.siteId || 'N/A',
          'Site Status': site.site_status || 'N/A',
          Region: site.region || 'N/A',
          Vendor: site.vendor || 'N/A',
          Physical: site.physical || 'N/A',
          'Coverage Type': site.coverage_type || 'N/A',
          BSC: site.bsc || 'N/A',
          'BSC Code': site.bsc_code || 'N/A',
          'Commercial City': site.commercial_city || 'N/A',
          'Commercial District': site.commercial_district || 'N/A',
          'Commercial Region': site.commercial_region || 'N/A',
          'Commercial Subregion': site.commercial_subregion || 'N/A',
          'Technical City': site.technical_city || 'N/A',
          MBU: site.mbu || 'N/A',
          'PTA District': site.pta_district || 'N/A',
          'PTA Province': site.pta_province || 'N/A',
          'PTA Tehsil': site.pta_tehsil || 'N/A',
          'USF Site': site.usf_site || 'N/A',
          'Locked Status': site.locked_status || 'N/A',
          Archived: site.archived || 'N/A',
          'Archived Type': site.archived_type || 'N/A',
          'Franchise ID': site.franchise_id || 'N/A',
          Prefix: site.prefix || 'N/A',
          'Site From': site.site_from || 'N/A',
          'Site To': site.site_to || 'N/A',
          'OMO Name': site.omo_name || 'N/A',
          'Sharing Type Host/Guest': site.sharing_type_hostguest || 'N/A',
          'Part of Dec CFO': site.part_of_dec_cfo || 'N/A',
          'City Prefix Based': site.city_prefix_based || 'N/A',
          '2G Locked Tag': site['2g_locked_tag'] || 'N/A',
          '4G Locked Tag': site['4g_locked_tag'] || 'N/A',
          Latitude: site.lat || 'N/A',
          Longitude: site.long || 'N/A',
          Status: site.status || 'N/A',
        });
      });
    });

    // Generate Excel file
    downloadExcelSheetFunction(excelData, 'auditLogs');
  };

  const handleCountClick = (sites, type, title) => {
    if (sites && sites.length > 0) {
      setSelectedSites(sites);
      setPopupType(type);
      setPopupTitle(title);
      setSitesPopup(true);
    }
  };

  const handleSiteClick = (site) => {
    setSelectedSite(site);
  };

  const handleCloseSitesList = () => {
    setSitesPopup(false);
    setSelectedSites([]);
    setSelectedSite(null);
    setPopupType('');
    setPopupTitle('');
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            backgroundColor: '#fafafa',
            minHeight: '600px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 16px 24px',
            backgroundColor: '#fff',
            borderRadius: '12px 12px 0 0',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#333',
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            Audit logs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handlePrint}
              sx={{
                color: '#666',
                '&:hover': { color: '#333' },
              }}
            >
              <PrintIcon />
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{
                color: '#666',
                '&:hover': { color: '#333' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: '0 40px 24px 24px',
            backgroundColor: '#fff',
            borderRadius: '0 0 12px 12px',
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={40} />
            </Box>
          ) : logs.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <Typography color="text.secondary" variant="body1">
                No audit logs found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {Object.entries(groupLogsByDate(logs)).map(([date, dateLogs]) => (
                <Box key={date} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#333',
                      fontWeight: 600,
                      fontSize: '14px',
                      mb: 2,
                      textTransform: 'uppercase',
                    }}
                  >
                    {date}
                  </Typography>

                  <Box sx={{ position: 'relative', pl: 12 }}>
                    {/* Timeline line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '90px',
                        top: 2,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: '#e0e0e0',
                      }}
                    />

                    {dateLogs.map((log, index) => (
                      <Box
                        key={log.id || index}
                        sx={{
                          position: 'relative',
                          mb: 2,
                          display: 'flex',
                          alignItems: 'flex-start',
                        }}
                      >
                        {/* Timeline dot */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '-12px',
                            top: '4px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: '#d32f2f',
                            border: '2px solid #f6dbdb',
                            boxShadow: '0 0 0 2px #f6dbdb',
                            zIndex: 1,
                          }}
                        />

                        {/* Time */}
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            left: '-120px',
                            top: '2px',
                            color: '#666',
                            fontSize: '12px',
                            fontWeight: 500,
                            minWidth: '100px',
                            textAlign: 'right',
                            zIndex: 2,
                          }}
                        >
                          {formatTime(log.created_at || log.timestamp || new Date())}
                        </Typography>

                        {/* Content */}
                        <Box
                          sx={{
                            ml: 2,
                            flex: 1,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            border: '1px solid #e9ecef',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '14px',
                              mb: 0.5,
                            }}
                          >
                            {log.username || log.user || 'Unknown User'}
                          </Typography>

                          {/* {log.summary || log.action || log.description || 'Action performed'} */}
                          {log.details &&
                            (() => {
                              const details = log.details;
                              let newCount = 0;
                              let updatedCount = 0;
                              let deletedCount = 0;

                              if (details.newSite !== undefined || details.newSites !== undefined) {
                                const newData = details.newSite || details.newSites;
                                newCount = Array.isArray(newData) ? newData.length : newData;
                              }

                              if (details.updatedSites !== undefined) {
                                updatedCount = Array.isArray(details.updatedSites)
                                  ? details.updatedSites.length
                                  : details.updatedSites;
                              }

                              if (
                                details.deletedSite !== undefined ||
                                details.deleteSites !== undefined ||
                                details.deletedSites !== undefined
                              ) {
                                const deletedData =
                                  details.deletedSite ||
                                  details.deleteSites ||
                                  details.deletedSites;
                                deletedCount = Array.isArray(deletedData)
                                  ? deletedData.length
                                  : deletedData;
                              }

                              const totalCount = newCount + updatedCount + deletedCount;

                              // Get the actual site data
                              const newSitesData = details.newSite || details.newSites;
                              const updatedSitesData = details.updatedSites;
                              const deletedSitesData =
                                details.deletedSite || details.deleteSites || details.deletedSites;

                              return totalCount > 0 ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    mt: 0.25,
                                    p: 0.2,
                                    mr: 4,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '2px',
                                    border: '1px solid #e9ecef',
                                  }}
                                >
                                  {/* Header Row */}
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 1fr 1fr 1fr',
                                      gap: 0.25,
                                      mb: 0.2,
                                      pb: 0.2,
                                      borderBottom: '1px solid #e9ecef',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: '#666',
                                        textAlign: 'center',
                                      }}
                                    >
                                      New
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: '#666',
                                        textAlign: 'center',
                                      }}
                                    >
                                      Updated
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: '#666',
                                        textAlign: 'center',
                                      }}
                                    >
                                      Deleted
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        color: '#666',
                                        textAlign: 'center',
                                      }}
                                    >
                                      Total
                                    </Typography>
                                  </Box>

                                  {/* Data Row */}
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 1fr 1fr 1fr',
                                      gap: 0.25,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: newCount > 0 ? 'pointer' : 'default',
                                        '&:hover':
                                          newCount > 0 ? { backgroundColor: '#e8f5e8' } : {},
                                        borderRadius: '2px',
                                        p: 0.1,
                                      }}
                                      onClick={() =>
                                        newCount > 0 &&
                                        handleCountClick(newSitesData, 'new', 'New Sites')
                                      }
                                    >
                                      <Box
                                        sx={{
                                          width: 5,
                                          height: 5,
                                          borderRadius: '50%',
                                          backgroundColor: '#28a745',
                                          mr: 0.2,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#333',
                                          fontSize: '10px',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {newCount}
                                      </Typography>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: updatedCount > 0 ? 'pointer' : 'default',
                                        '&:hover':
                                          updatedCount > 0 ? { backgroundColor: '#fff8e1' } : {},
                                        borderRadius: '2px',
                                        p: 0.1,
                                      }}
                                      onClick={() =>
                                        updatedCount > 0 &&
                                        handleCountClick(
                                          updatedSitesData,
                                          'updated',
                                          'Updated Sites'
                                        )
                                      }
                                    >
                                      <Box
                                        sx={{
                                          width: 5,
                                          height: 5,
                                          borderRadius: '50%',
                                          backgroundColor: '#ffc107',
                                          mr: 0.2,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#333',
                                          fontSize: '10px',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {updatedCount}
                                      </Typography>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: deletedCount > 0 ? 'pointer' : 'default',
                                        '&:hover':
                                          deletedCount > 0 ? { backgroundColor: '#ffebee' } : {},
                                        borderRadius: '2px',
                                        p: 0.1,
                                      }}
                                      onClick={() =>
                                        deletedCount > 0 &&
                                        handleCountClick(
                                          deletedSitesData,
                                          'deleted',
                                          'Deleted Sites'
                                        )
                                      }
                                    >
                                      <Box
                                        sx={{
                                          width: 4,
                                          height: 4,
                                          borderRadius: '50%',
                                          backgroundColor: '#dc3545',
                                          mr: 0.2,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#333',
                                          fontSize: '10px',
                                          fontWeight: 500,
                                        }}
                                      >
                                        {deletedCount}
                                      </Typography>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '2px',
                                        p: 0.1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#333',
                                          fontSize: '10px',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {totalCount}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ) : null;
                            })()}
                          {!log.details && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#666',
                                fontSize: '13px',
                                lineHeight: 1.4,
                              }}
                            >
                              {log.summary || log.action || log.description || 'Action performed'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Sites List Popup */}
      <Dialog open={sitesPopup} onClose={handleCloseSitesList} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{popupTitle}</Typography>
            <Button onClick={handleCloseSitesList} sx={{ minWidth: 'auto', p: 0 }}>
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Sites List Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                {popupType === 'deleted' ? 'Deleted Sites' : 'Available Sites'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  maxHeight: '120px',
                  overflowY: 'auto',
                  p: 1,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  border: '1px solid #e9ecef',
                }}
              >
                {selectedSites.map((site, index) => (
                  <Box
                    key={index}
                    onClick={popupType === 'deleted' ? undefined : () => handleSiteClick(site)}
                    sx={{
                      cursor: popupType === 'deleted' ? 'default' : 'pointer',
                      p: 1,
                      borderRadius: 1,
                      backgroundColor:
                        selectedSite === site && popupType !== 'deleted' ? '#e3f2fd' : '#fff',
                      border:
                        selectedSite === site && popupType !== 'deleted'
                          ? '2px solid #1976d2'
                          : '1px solid #e0e0e0',
                      minWidth: '120px',
                      textAlign: 'center',
                      transition: popupType === 'deleted' ? 'none' : 'all 0.2s ease',
                      '&:hover':
                        popupType === 'deleted'
                          ? {}
                          : {
                              backgroundColor: selectedSite === site ? '#e3f2fd' : '#f5f5f5',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                      Code: {site.site_code}
                    </Typography>
                    {popupType !== 'deleted' && site.region && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '10px' }}
                      >
                        {site.region}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
            {/* Site Details Section */}
            {selectedSite && popupType !== 'deleted' && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                  {popupType === 'deleted' ? 'Deletion Details' : 'Site Information'}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    p: 2,
                  }}
                >
                  {popupType === 'deleted' ? (
                    // Deleted site details
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#d32f2f' }}
                        >
                          Deletion Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                            border: '1px solid #ff9800',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#e65100', fontSize: '10px' }}
                          >
                            Site ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.siteId || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    // Regular site details
                    <Grid container spacing={2}>
                      {/* Basic Information */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}
                        >
                          Basic Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e8f5e8',
                            borderRadius: 1,
                            border: '1px solid #4caf50',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '10px' }}
                          >
                            Site Code
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.site_code || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e8f5e8',
                            borderRadius: 1,
                            border: '1px solid #4caf50',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '10px' }}
                          >
                            Status
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.site_status || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e8f5e8',
                            borderRadius: 1,
                            border: '1px solid #4caf50',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '10px' }}
                          >
                            Region
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.region || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e8f5e8',
                            borderRadius: 1,
                            border: '1px solid #4caf50',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '10px' }}
                          >
                            Vendor
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.vendor || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Technical Information */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}
                        >
                          Technical Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e3f2fd',
                            borderRadius: 1,
                            border: '1px solid #2196f3',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#1565c0', fontSize: '10px' }}
                          >
                            Physical
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.physical || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e3f2fd',
                            borderRadius: 1,
                            border: '1px solid #2196f3',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#1565c0', fontSize: '10px' }}
                          >
                            Coverage Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.coverage_type || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e3f2fd',
                            borderRadius: 1,
                            border: '1px solid #2196f3',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#1565c0', fontSize: '10px' }}
                          >
                            BSC
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.bsc || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#e3f2fd',
                            borderRadius: 1,
                            border: '1px solid #2196f3',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#1565c0', fontSize: '10px' }}
                          >
                            BSC Code
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.bsc_code || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Commercial Information */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#1976d2', mt: 1 }}
                        >
                          Commercial Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fce4ec',
                            borderRadius: 1,
                            border: '1px solid #e91e63',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#ad1457', fontSize: '10px' }}
                          >
                            Commercial City
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.commercial_city || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fce4ec',
                            borderRadius: 1,
                            border: '1px solid #e91e63',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#ad1457', fontSize: '10px' }}
                          >
                            Commercial District
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.commercial_district || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fce4ec',
                            borderRadius: 1,
                            border: '1px solid #e91e63',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#ad1457', fontSize: '10px' }}
                          >
                            Commercial Region
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.commercial_region || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fce4ec',
                            borderRadius: 1,
                            border: '1px solid #e91e63',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#ad1457', fontSize: '10px' }}
                          >
                            Commercial Subregion
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.commercial_subregion || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fce4ec',
                            borderRadius: 1,
                            border: '1px solid #e91e63',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#ad1457', fontSize: '10px' }}
                          >
                            Technical City
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.technical_city || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fce4ec',
                            borderRadius: 1,
                            border: '1px solid #e91e63',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#ad1457', fontSize: '10px' }}
                          >
                            MBU
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.mbu || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* PTA Information */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#1976d2', mt: 1 }}
                        >
                          PTA Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff8e1',
                            borderRadius: 1,
                            border: '1px solid #ffc107',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#f57f17', fontSize: '10px' }}
                          >
                            PTA District
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.pta_district || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff8e1',
                            borderRadius: 1,
                            border: '1px solid #ffc107',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#f57f17', fontSize: '10px' }}
                          >
                            PTA Province
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.pta_province || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff8e1',
                            borderRadius: 1,
                            border: '1px solid #ffc107',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#f57f17', fontSize: '10px' }}
                          >
                            PTA Tehsil
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.pta_tehsil || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff8e1',
                            borderRadius: 1,
                            border: '1px solid #ffc107',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#f57f17', fontSize: '10px' }}
                          >
                            USF Site
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.usf_site || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Additional Information */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#1976d2', mt: 1 }}
                        >
                          Additional Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Locked Status
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.locked_status || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Archived
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.archived || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Archived Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.archived_type || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Franchise ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.franchise_id || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Prefix
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.prefix || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Site From
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.site_from || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Site To
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.site_to || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            OMO Name
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.omo_name || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Sharing Type Host/Guest
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.sharing_type_hostguest || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            Part of Dec CFO
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.part_of_dec_cfo || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#f1f8e9',
                            borderRadius: 1,
                            border: '1px solid #8bc34a',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#558b2f', fontSize: '10px' }}
                          >
                            City Prefix Based
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite.city_prefix_based || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Locked Tags */}
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: '#1976d2', mt: 1 }}
                        >
                          Locked Tags
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                            border: '1px solid #ff9800',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#e65100', fontSize: '10px' }}
                          >
                            2G Locked Tag
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite['2g_locked_tag'] || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            p: 0.5,
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                            border: '1px solid #ff9800',
                            minHeight: '45px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: '#e65100', fontSize: '10px' }}
                          >
                            4G Locked Tag
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '11px' }}>
                            {selectedSite['4g_locked_tag'] || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
